/**
 * Trae todas las páginas de una lista, en paralelo y en orden.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO NO VA EN FILA INDIA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * El punto de venta descarga el catálogo entero para poder vender sin internet.
 * Antes pedía la página 1, **esperaba**, pedía la 2, esperaba… Con los 3.470
 * productos de un negocio real son 18 viajes seguidos al servidor: a 200 ms cada
 * uno, casi cuatro segundos en los que el POS no sirve. Pasa la primera vez que
 * alguien abre Softfi en un aparato nuevo, y cada vez que se limpia la copia.
 *
 * Y esperar no aportaba nada: la página 5 no depende de la 4. Lo único que hay
 * que saber antes es CUÁNTAS hay, y eso lo dice la primera respuesta.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ DE SEIS EN SEIS, NO LAS DIECIOCHO DE GOLPE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Soltar dieciocho peticiones a la vez desde el teléfono de una bodega con wifi
 * flojo no es más rápido: se pelean por el mismo ancho de banda, y el navegador
 * encola de todas formas las que pasan de seis por dominio. Al servidor, además,
 * le llegaría una ráfaga por cada aparato que abre la caja por la mañana — y
 * todos abren a la misma hora.
 *
 * Seis es lo que el navegador deja correr de verdad en paralelo. Pedir más no
 * llega antes; solo alarga la cola en otro sitio.
 */

/** Lo mínimo que tiene que devolver el servidor para poder paginar. */
export type PaginaDeLista<T> = {
  items: T[];
  pages: number;
};

export type OpcionesDePaginado = {
  /** Cuántos elementos trae cada página. */
  porPagina: number;
  /** Tope de elementos que se guardan, pase lo que pase. */
  tope: number;
  /** Cuántas peticiones vuelan a la vez. Seis salvo que haya un motivo. */
  aLaVez?: number;
};

export type DescargaConTope<T> = {
  items: T[];
  /**
   * `true` si había MÁS de lo que cabe en el tope y lo demás se quedó fuera.
   *
   * ⚠️ Antes el corte era mudo: un negocio con más productos que el tope veía
   * un catálogo al que le faltaban cosas y nadie le decía por qué. Lo encontró
   * la auditoría. Con esto, la caja lo puede decir.
   */
  recortado: boolean;
};

/** Lo mismo que `descargarPaginadoConTope`, cuando solo interesan los elementos. */
export async function descargarPaginado<T>(
  pedir: (pagina: number) => Promise<PaginaDeLista<T>>,
  opciones: OpcionesDePaginado,
): Promise<T[]> {
  return (await descargarPaginadoConTope(pedir, opciones)).items;
}

export async function descargarPaginadoConTope<T>(
  pedir: (pagina: number) => Promise<PaginaDeLista<T>>,
  { porPagina, tope, aLaVez = 6 }: OpcionesDePaginado,
): Promise<DescargaConTope<T>> {
  const primera = await pedir(1);
  const acumulado = [...primera.items];

  // Cuántas páginas hacen falta de verdad: ni más de las que hay, ni más de las
  // que caben en el tope. Sin lo segundo, un supermercado con 40.000 productos
  // pediría 200 páginas para tirar 195.
  const necesarias = Math.min(primera.pages, Math.ceil(tope / porPagina));

  const pendientes: number[] = [];
  for (let p = 2; p <= necesarias; p++) pendientes.push(p);

  // Se guardan POR NÚMERO DE PÁGINA, no en el orden en que contesten. Así el
  // catálogo sale siempre igual aunque el servidor responda desordenado — y en
  // una lista ordenada por nombre, que el orden dependa de la red sería un
  // catálogo distinto en cada aparato.
  const porNumero = new Map<number, T[]>();

  for (let i = 0; i < pendientes.length; i += aLaVez) {
    const tanda = pendientes.slice(i, i + aLaVez);
    const respuestas = await Promise.all(tanda.map(pedir));
    tanda.forEach((pagina, j) => {
      const respuesta = respuestas[j];
      if (respuesta) porNumero.set(pagina, respuesta.items);
    });
  }

  for (let p = 2; p <= necesarias; p++) {
    acumulado.push(...(porNumero.get(p) ?? []));
  }

  return {
    items: acumulado.slice(0, tope),
    // Quedaron páginas sin pedir, o la última pedida se pasó del tope.
    recortado: primera.pages > necesarias || acumulado.length > tope,
  };
}
